import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useClientTableSort, {
  sortRowsByConfig,
} from "../../hooks/useClientTableSort";
import Select from "../../components/ReportSelect";
import {
  Container,
  Table,
  Button,
  Row,
  Col,
  Spinner,
  Modal,
} from "react-bootstrap";
import Pagination from "../../components/Pagination";
import { saveAs } from "file-saver";
import ReportFilterPanel from "../../components/ReportFilterPanel";
import { filterReportRows } from "../../utils/reportSearch";
import "../../styles/Reports.css";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ITEMS_PER_PAGE = 10;

const formatDate = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export default function AssociateTurnoverReport() {
  const tableRef = useRef(null);
  const navigate = useNavigate();

  // -------------------------
  // Dropdowns
  // -------------------------
  const [orgUnits, setOrgUnits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCats, setSubCats] = useState([]);
  const [groups, setGroups] = useState([]);

  // -------------------------
  // Filters (UI)
  // -------------------------
  const [pStartDate, setPStartDate] = useState("");
  const [pEndDate, setPEndDate] = useState("");
  const [pOrgUnit, setPOrgUnit] = useState(null);
  const [pCategory, setPCategory] = useState(null);
  const [pSubCat, setPSubCat] = useState(null);
  const [pGroup, setPGroup] = useState(null);
  const [pSearch, setPSearch] = useState("");

  useEffect(() => {
    const today = new Date();

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);

    const defaultStart = formatDate(oneMonthAgo);
    const defaultEnd = formatDate(today);

    setPStartDate(defaultStart);
    setPEndDate(defaultEnd);
  }, []);

  // -------------------------
  // Data state
  // -------------------------
  const [filters, setFilters] = useState({});
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // -------------------------
  // Modal state
  // -------------------------
  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [gainedList, setGainedList] = useState([]);
  const [lostList, setLostList] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  // -------------------------
  // Load dropdowns
  // -------------------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    async function loadDropdowns() {
      try {
        const [ou, cat, sc, grp] = await Promise.all([
          axios.get(`${BASE_URL}/api/dropdowns/org-units`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/sub-categories`, { headers }),
          axios.get(`${BASE_URL}/api/dropdowns/position-groups`, { headers }),
        ]);

        setOrgUnits(ou.data.map((o) => ({ value: o.ou_id, label: o.ou_name })));
        setCategories(
          cat.data.map((c) => ({ value: c.c_id, label: c.c_name }))
        );
        setSubCats(sc.data.map((s) => ({ value: s.sc_id, label: s.sc_name })));
        setGroups(grp.data.map((g) => ({ value: g.pg_id, label: g.pg_name })));
      } catch (err) {
        console.error("Dropdown load error:", err);
      }
    }

    loadDropdowns();
  }, []);

  // -------------------------
  // Fetch data (ONLY after Filter)
  // -------------------------
  const fetchRows = useCallback(async () => {
    if (!hasSearched) return;

    setLoading(true);
    setCurrentPage(1);

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get(
        `${BASE_URL}/api/reports/associate-turnover`,
        {
          headers,
          params: filters,
        }
      );

      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Associate turnover fetch error:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filters, hasSearched]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // -------------------------
  // Apply filters (Filter button)
  // -------------------------
  const applyFilters = () => {
    setHasSearched(true);
    setFilters({
      startDate: pStartDate || "",
      endDate: pEndDate || "",
      orgUnit: pOrgUnit?.value || "",
      category: pCategory?.value || "",
      subCategory: pSubCat?.value || "",
      group: pGroup?.value || "",
    });
  };

  // -------------------------
  // Open modal
  // -------------------------
  const openModal = async (row) => {
    setSelectedRow(row);
    setShowModal(true);
    setModalLoading(true);

    try {
      const token = localStorage.getItem("token");

      const res = await axios.get(
        `${BASE_URL}/api/reports/associate-turnover-details`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            recruiter: row.recruiter_id,
            startDate: pStartDate,
            endDate: pEndDate,
          },
        }
      );

      setGainedList(res.data.gained || []);
      setLostList(res.data.lost || []);
    } catch (err) {
      console.error("Modal load error:", err);
      setGainedList([]);
      setLostList([]);
    } finally {
      setModalLoading(false);
    }
  };

  // -------------------------
  // Clear filters
  // -------------------------
  const clearFilters = () => {
    setPStartDate("");
    setPEndDate("");
    setPOrgUnit(null);
    setPCategory(null);
    setPSubCat(null);
    setPGroup(null);
    setPSearch("");

    setFilters({});
    setRows([]);
    setHasSearched(false);
    setCurrentPage(1);
  };

  // -------------------------
  // Pagination
  // -------------------------
  const searchedRows = filterReportRows(rows, pSearch);
  const sortConfig = useClientTableSort(tableRef, () => setCurrentPage(1));
  const sortedRows = sortRowsByConfig(searchedRows, sortConfig, [
    (row) => row.recruiter,
    (row) => row.average_associates,
    (row) => row.associates_gained,
    (row) => row.associates_lost,
    (row) => row.turnover_rate,
  ]);
  const paginated = sortedRows.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // -------------------------
  // Export CSV
  // -------------------------
  const exportCSV = () => {
    if (!hasSearched || searchedRows.length === 0) {
      alert("No data to export.");
      return;
    }

    const headers = [
      "Recruiter",
      "Average Associates",
      "Associates Gained",
      "Associates Lost",
      "Turnover %",
    ];

    const lines = searchedRows.map((r) =>
      [
        r.recruiter,
        r.average_associates,
        r.associates_gained,
        r.associates_lost,
        r.turnover_rate,
      ]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );

    const blob = new Blob([[headers.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });

    saveAs(blob, "AssociateTurnoverReport.csv");
  };

  // -------------------------
  // Render
  // -------------------------
  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-4">
        <h3 className="report-page-title fw-bold m-0"><span className="report-title-icon" aria-hidden="true"><i className="fas fa-chart-bar" /></span><span>Associate Turnover Report</span></h3>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => navigate("/reports")}
        >
          ⬅ Return to Report List
        </Button>
      </div>

      {/* Filters */}
      <ReportFilterPanel>
      <Row className="mb-3 g-3 align-items-end report-row">
        <Col>
          <label>Start Date</label>
          <input
            type="date"
            className="form-control"
            value={pStartDate}
            onChange={(e) => setPStartDate(e.target.value)}
          />
        </Col>

        <Col>
          <label>End Date</label>
          <input
            type="date"
            className="form-control"
            value={pEndDate}
            onChange={(e) => setPEndDate(e.target.value)}
          />
        </Col>

        <Col>
          <label>Org Unit</label>
          <Select
            value={pOrgUnit}
            onChange={setPOrgUnit}
            options={orgUnits}
            isClearable
          />
        </Col>

        <Col>
          <label>Groups</label>
          <Select
            value={pGroup}
            onChange={setPGroup}
            options={groups}
            isClearable
          />
        </Col>
      </Row>

      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md="3">
          <label>Category</label>
          <Select
            value={pCategory}
            onChange={setPCategory}
            options={categories}
            isClearable
          />
        </Col>

        <Col md="3">
          <label>Sub-Category</label>
          <Select
            value={pSubCat}
            onChange={setPSubCat}
            options={subCats}
            isClearable
          />
        </Col>
      </Row>

      <Row className="mb-3 g-3 align-items-end report-row">
        <Col md={3}>
          <label>Search</label>
          <input type="text" className="form-control" placeholder="ID / Name / Keyword" value={pSearch} onChange={(event) => setPSearch(event.target.value)} />
        </Col>
      </Row>
      </ReportFilterPanel>
      <Row className="mb-3 g-3 align-items-end report-row report-actions-row">
        <Col md="auto" className="d-flex align-items-end">
          <Button className="me-2" onClick={applyFilters}>
            <i className="fas fa-filter" aria-hidden="true" /> Filter
          </Button>
          <Button variant="secondary" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Col>

        <Col md="auto" className="ms-auto d-flex align-items-end">
          <Button
            className="btn-export"
            onClick={exportCSV}
            disabled={!hasSearched || searchedRows.length === 0}
          >
            Export CSV
          </Button>
        </Col>
      </Row>

      {/* Table */}
      {!hasSearched ? (
        <div className="text-center text-muted py-5">
          Use filters above and click <strong>Filter</strong> to load results.
        </div>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <div className="table-responsive">
          <Table ref={tableRef} data-nw-client-sort="true" bordered hover size="sm">
            <thead className="table-dark text-center">
              <tr>
                <th>Recruiter</th>
                <th>Average Associates</th>
                <th>Associates Gained</th>
                <th>Associates Lost</th>
                <th>Turnover %</th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center text-muted">
                    No results found.
                  </td>
                </tr>
              ) : (
                paginated.map((r, i) => (
                  <tr
                    key={i}
                    className="text-center clickable-row"
                    onClick={() => openModal(r)}
                  >
                    <td>{r.recruiter}</td>
                    <td>{r.average_associates}</td>
                    <td>{r.associates_gained}</td>
                    <td>{r.associates_lost}</td>
                    <td>{r.turnover_rate}%</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Pagination
            currentPage={currentPage}
            totalItems={sortedRows.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedRow?.recruiter}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {modalLoading ? (
            <div className="text-center my-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              {/* Summary */}
              <Row className="mb-3">
                <Col>
                  <strong>Start Date:</strong> {pStartDate}
                </Col>
                <Col>
                  <strong>Associates Lost / Avg:</strong>{" "}
                  {selectedRow?.associates_lost} /{" "}
                  {selectedRow?.average_associates}
                </Col>
              </Row>

              <Row className="mb-4">
                <Col>
                  <strong>End Date:</strong> {pEndDate}
                </Col>
                <Col>
                  <strong>Turnover %:</strong> {selectedRow?.turnover_rate}%
                </Col>
              </Row>

              {/* GAINED */}
              <h5 className="mt-3">Associates Gained ({gainedList.length})</h5>
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th>First</th>
                    <th>Last</th>
                    <th>Category</th>
                    <th>Sub Category</th>
                    <th>Org Unit</th>
                    <th>Position</th>
                  </tr>
                </thead>
                <tbody>
                  {gainedList.map((g, i) => (
                    <tr key={i}>
                      <td>{g.first_name}</td>
                      <td>{g.last_name}</td>
                      <td>{g.category}</td>
                      <td>{g.sub_category}</td>
                      <td>{g.org_unit}</td>
                      <td>{g.position}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* LOST */}
              <h5 className="mt-4">Associates Lost ({lostList.length})</h5>
              <Table bordered size="sm">
                <thead>
                  <tr>
                    <th>First</th>
                    <th>Last</th>
                    <th>Category</th>
                    <th>Sub Category</th>
                    <th>Org Unit</th>
                    <th>Position</th>
                  </tr>
                </thead>
                <tbody>
                  {lostList.map((l, i) => (
                    <tr key={i}>
                      <td>{l.first_name}</td>
                      <td>{l.last_name}</td>
                      <td>{l.category}</td>
                      <td>{l.sub_category}</td>
                      <td>{l.org_unit}</td>
                      <td>{l.position}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
